import { useEffect } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ACCOUNT_QUERY_KEY } from '../hooks/accountQuery';
import { useAccount } from '../hooks/useAccount';
import { useDocumentHead } from '../hooks/useDocumentHead';
import { api, ApiError } from '../lib/api';
import { Button } from '../components/ui/Button';
import { Note } from '../components/ui/Note';
import { Panel } from '../components/ui/Panel';

/**
 * Tokens attempted this page load. Survives React Strict Mode's mount → unmount
 * → remount, which would otherwise POST the same one-use token twice: first
 * succeeds, second 401s, and the error branch paints even though the cookie
 * is already set.
 */
const attemptedThisLoad = new Set<string>();

/**
 * Where the magic link lands: `${APP_URL}/auth/verify?token=…`.
 *
 * This is a held URL, it's in an email that may already be sitting in an
 * inbox, so the route cannot move (see AGENTS.md's routing gotcha).
 *
 * The token is exchanged once, on mount, for a session cookie.
 */
export function VerifyPage() {
    useDocumentHead({
        title: 'Verify sign-in · ActualSold',
        description: 'Completing your magic-link sign-in.',
        canonicalPath: '/auth/verify',
        noindex: true,
    });

    const [params] = useSearchParams();
    const navigate = useNavigate();
    const client = useQueryClient();
    // Link re-click after success still works: session exists, skip exchange.
    const account = useAccount();

    const token = params.get('token') ?? '';

    const verify = useMutation({
        mutationFn: () => api.verifyLink(token),
        onSuccess: async () => {
            await client.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEY });
            navigate('/app', { replace: true });
        },
        onError: () => {
            // Prefer landing on the form they need next rather than a dead-end
            // panel. Message is read from the query string on SignInPage.
            navigate('/signin?reason=link_expired', { replace: true });
        },
    });

    const { mutate } = verify;

    useEffect(() => {
        if (!token || attemptedThisLoad.has(token)) return;
        // Wait for the session probe so a reopened/prefetched link does not
        // 401 a still-valid cookie into a "get a new link" redirect.
        if (account.isPending) return;
        if (account.data) return;
        attemptedThisLoad.add(token);
        mutate();
    }, [token, mutate, account.isPending, account.data]);

    if (!token) {
        return (
            <Panel title="That link is missing its token">
                <p className="text-sm text-text-muted">
                    Ask for a new one on the{' '}
                    <Link to="/signin" className="underline">
                        sign-in page
                    </Link>
                    .
                </p>
            </Panel>
        );
    }

    if (account.data) return <Navigate to="/app" replace />;

    // Fail-safe if navigation did not run (tests, unexpected race). Production
    // path uses onError → /signin; keep the panel so the page is never blank.
    if (verify.error) {
        return (
            <Panel title="That link didn't work">
                <Note tone="warn">
                    {verify.error instanceof ApiError ? verify.error.message : 'Something went wrong.'}
                </Note>
                <p className="mt-4 text-sm text-text-muted">
                    Links work once and expire after 15 minutes.
                </p>
                <div className="mt-5">
                    <Button type="button" onClick={() => navigate('/signin?reason=link_expired', { replace: true })}>
                        Request a new link
                    </Button>
                </div>
            </Panel>
        );
    }

    return <p className="py-16 text-center text-sm text-text-muted">Signing you in…</p>;
}
